import React, { useState } from 'react';
import cx from 'classnames';
import * as _ from 'lodash';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ReactTooltip from 'react-tooltip';

import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { makeStyles } from 'design-system/product/theme';
import NucleusLoadingSpinnerFullWrapper from 'nucleus/components/NucleusLoadingSpinnerFullWrapper';
import NucleusLoadingSpinner from 'nucleus/components/NucleusLoadingSpinner';

let globalButtonTooltipId = 0;

type NucleusButtonProps = {
  primary?: boolean;
  secondary?: boolean;
  tetriary?: boolean;
  base?: boolean;
  icon?: IconProp;
  customIcon?: React.ReactNode;
  iconPosition?: 'left' | 'right';
  tooltipText?: string | null;
  loading?: boolean;
  active?: boolean;
  id?: string;
  full?: boolean;
  fullBetween?: boolean;
  fullText?: boolean;
  fullTextWidth?: boolean;
};

// react-tooltip doesn't work on disabled elements.
// This is a workaround suggested in the open issue:
// https://github.com/wwayne/react-tooltip/issues/304
// TODO: Find a better alternative than react-tooltip and then remove this wrapper span
const TooltipWorkaroundSpan = ({
  children,
  className,
  tooltipId,
  tooltipText,
}: {
  children: React.ReactNode;
  className?: string;
  tooltipId: string;
  tooltipText?: string | null;
}) => (
  <span data-for={tooltipId} data-tip={tooltipText} className={className}>
    {children}
  </span>
);

const NucleusButton: React.FC<
  NucleusButtonProps & React.ButtonHTMLAttributes<HTMLButtonElement>
> = props => {
  const classes = useStyles();

  const type = props.secondary
    ? 'secondary'
    : props.tetriary
    ? 'tetriary'
    : props.base
    ? 'base'
    : 'primary';

  const [tooltipId] = useState('button-tooltip-' + globalButtonTooltipId++);
  const titleHelper =
    !props.tooltipText && typeof props.children === 'string' ? props.children : '';

  return (
    <>
      <TooltipWorkaroundSpan
        className={props.className}
        tooltipId={tooltipId}
        tooltipText={props.tooltipText}
      >
        <button
          {..._.omit(
            props,
            'className',
            'primary',
            'secondary',
            'tetriary',
            'base',
            'tooltipText',
            'iconPosition',
            'icon',
            'customIcon',
            'loading',
            'active',
            'full',
            'fullText',
          )}
          className={cx(classes.wrapper, classes[type], {
            [classes.loading]: props.loading,
            [classes.active]: props.active,
            [classes.full]: props.full,
            [classes.fullBetween]: props.fullBetween,
          })}
          id={props.id}
          title={titleHelper}
        >
          {props.loading && (
            <NucleusLoadingSpinnerFullWrapper position="absolute">
              <NucleusLoadingSpinner size={20} />
            </NucleusLoadingSpinnerFullWrapper>
          )}
          {props.children && (
            <span
              className={cx(
                props.fullText ? classes.textFull : classes.textTruncated,
                props.iconPosition === 'left' && classes.textRight,
                props.fullTextWidth && classes.fullTextWidth,
              )}
            >
              {props.children}
            </span>
          )}
          {props.icon && (
            <FontAwesomeIcon
              icon={props.icon}
              className={cx(classes.icon, props.iconPosition === 'left' && classes.iconLeft)}
            />
          )}
          {props.customIcon && (
            <div className={cx(classes.icon, props.iconPosition === 'left' && classes.iconLeft)}>
              {props.customIcon}
            </div>
          )}
        </button>
      </TooltipWorkaroundSpan>
      {props.tooltipText && <ReactTooltip id={tooltipId} effect="solid" />}
    </>
  );
};

export default NucleusButton;

const useStyles = makeStyles(theme => ({
  wrapper: {
    fontWeight: 500,
    fontSize: theme.typography.fontSize,
    background: 'transparent',
    height: theme.spacing(4),
    border: 1,
    borderRadius: 8,
    borderStyle: 'solid',
    padding: `0 ${theme.spacing(2)}px`,
    display: 'flex',
    alignItems: 'center',
    gap: `${theme.spacing(1)}px`,
    position: 'relative',
    overflow: 'hidden',
    '&:disabled': {
      opacity: 0.5,
      cursor: 'not-allowed',
    },
    '&:not(:disabled)': {
      cursor: 'pointer',
    },
  },
  primary: {
    color: theme.colors.white,
    background: theme.palette.primary.main,
    borderColor: theme.palette.primary.main,
  },
  secondary: {
    color: theme.palette.primary.main,
    background: theme.colors.white,
    borderColor: theme.palette.primary.main,
    '&$active': {
      background: theme.colors.periwinkle5,
    },
    transition: 'background 0.3s',
  },
  tetriary: {
    color: theme.palette.primary.main,
    background: theme.colors.white,
    borderColor: theme.colors.periwinkle50,
    '&$active': {
      background: theme.colors.periwinkle5,
    },
  },
  base: {
    color: theme.colors.black,
    background: theme.colors.gray10,
    border: 0,
  },
  icon: {
    width: theme.spacing(2),
    height: theme.spacing(2),
    flexShrink: 0,
  },
  iconLeft: {
    order: 1,
  },
  textTruncated: {
    display: 'block',
    ...theme.typography.ellipsis,
  },
  textFull: {
    display: 'block',
    whiteSpace: 'nowrap',
  },
  fullTextWidth: {
    width: '100%',
  },
  textRight: {
    order: 2,
  },
  loading: {
    pointerEvents: 'none',
    '& $icon, & > span': {
      opacity: 0,
    },
  },
  active: {
    cursor: 'pointer',
  },
  full: {
    width: '100%',
    justifyContent: 'center',
  },
  fullBetween: {
    width: '100%',
    justifyContent: 'space-between',
  },
}));
